// Game management service
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { v4 as uuidv4 } from 'uuid';

// Create a new game
export const createGame = async (gameData) => {
  const gameId = uuidv4();
  const gameRef = doc(db, 'games', gameId);

  const game = {
    competitionId: gameData.competitionId,
    categoryId: gameData.categoryId,
    name: gameData.name.trim(),
    date: gameData.date,
    startTime: gameData.startTime || null,
    status: 'planned',
    notes: gameData.notes || null,
    knockoutMode: gameData.knockoutMode || false,
    // Link to league game library (for duration tracking)
    ...(gameData.gameSectionId && { gameSectionId: gameData.gameSectionId }),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  await setDoc(gameRef, game);

  return {
    id: gameId,
    ...game
  };
};

// Get game by ID
export const getGame = async (gameId) => {
  const gameRef = doc(db, 'games', gameId);
  const gameSnap = await getDoc(gameRef);

  if (!gameSnap.exists()) {
    return null;
  }

  return {
    id: gameSnap.id,
    ...gameSnap.data()
  };
};

// Get all games for a competition
export const getGamesForCompetition = async (competitionId) => {
  const gamesRef = collection(db, 'games');
  const q = query(
    gamesRef,
    where('competitionId', '==', competitionId),
    orderBy('date', 'asc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Get games for a category
export const getGamesForCategory = async (categoryId) => {
  const gamesRef = collection(db, 'games');
  const q = query(
    gamesRef,
    where('categoryId', '==', categoryId),
    orderBy('date', 'asc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Get games by status
export const getGamesByStatus = async (competitionId, status) => {
  const gamesRef = collection(db, 'games');
  const q = query(
    gamesRef,
    where('competitionId', '==', competitionId),
    where('status', '==', status),
    orderBy('date', 'asc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Update game
export const updateGame = async (gameId, updates) => {
  const gameRef = doc(db, 'games', gameId);
  await updateDoc(gameRef, {
    ...updates,
    updatedAt: serverTimestamp()
  });
};

// Update game status
export const updateGameStatus = async (gameId, status) => {
  await updateGame(gameId, { status });
};

// Mark game as played
export const markGameAsPlayed = async (gameId) => {
  await updateGameStatus(gameId, 'played');
};

// Void a game
export const voidGame = async (gameId) => {
  await updateGameStatus(gameId, 'voided');
};

// Update game category
export const updateGameCategory = async (gameId, categoryId) => {
  await updateGame(gameId, { categoryId });
};

// Draw playing order for a game
export const drawPlayingOrder = async (gameId, playerIds, organizerId) => {
  // Validate game status
  const game = await getGame(gameId);
  if (!game) {
    throw new Error('Game not found');
  }
  if (game.status !== 'planned') {
    throw new Error('Can only draw order for planned games');
  }

  // Shuffle player IDs using Fisher-Yates algorithm
  const shuffled = [...playerIds];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  // Get current draw version
  const currentVersion = game.drawVersion || 0;

  // Update game with drawn order
  const gameRef = doc(db, 'games', gameId);
  await updateDoc(gameRef, {
    drawnOrder: shuffled,
    drawnAt: serverTimestamp(),
    drawnBy: organizerId,
    drawVersion: currentVersion + 1,
    updatedAt: serverTimestamp()
  });

  return shuffled;
};

// Clear playing order (if needed for testing)
export const clearPlayingOrder = async (gameId) => {
  const gameRef = doc(db, 'games', gameId);
  await updateDoc(gameRef, {
    drawnOrder: null,
    drawnAt: null,
    drawnBy: null,
    drawVersion: 0,
    updatedAt: serverTimestamp()
  });
};

// Reset a game completely - deletes all results and returns to planned state
export const resetGame = async (gameId) => {
  // Import here to avoid circular dependency
  const { deleteAllResultsForGame } = await import('./resultService');

  // Delete all results for this game
  await deleteAllResultsForGame(gameId);

  // Reset game to planned state, clearing draw and status
  const gameRef = doc(db, 'games', gameId);
  await updateDoc(gameRef, {
    status: 'planned',
    drawnOrder: null,
    drawnAt: null,
    drawnBy: null,
    drawVersion: 0,
    updatedAt: serverTimestamp()
  });
};

// Delete a pending game (only allowed for planned games)
export const deletePendingGame = async (gameId) => {
  // Validate game status
  const game = await getGame(gameId);
  if (!game) {
    throw new Error('Game not found');
  }
  if (game.status !== 'planned') {
    throw new Error('Can only delete planned games. Use void instead for played games.');
  }

  // Import here to avoid circular dependency
  const { deleteAllResultsForGame } = await import('./resultService');

  // Delete any results (shouldn't be any for planned games, but just in case)
  await deleteAllResultsForGame(gameId);

  // Delete the game
  const gameRef = doc(db, 'games', gameId);
  await deleteDoc(gameRef);
};

// Fix playing games - Update all games with actualStartTime but no actualEndTime to status "playing"
// Also fix games that have ended to status "played"
export const fixPlayingGamesStatus = async (competitionId) => {
  const games = await getGamesForCompetition(competitionId);

  let updatedCount = 0;

  for (const game of games) {
    // If game has ended, set to "played"
    if (game.actualStartTime && game.actualEndTime && game.status !== 'played') {
      await updateGameStatus(game.id, 'played');
      updatedCount++;
    }
    // If game has started but not ended, set to "playing"
    else if (game.actualStartTime && !game.actualEndTime && game.status !== 'playing') {
      await updateGameStatus(game.id, 'playing');
      updatedCount++;
    }
  }

  return updatedCount;
};
